import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
	Form,
	Input,
	Button,
	Modal,
	Dropdown,
	Row,
	Col,
	Table,
	Card,
	notification,
	Tag,
	Checkbox,
} from "antd";
import { UnorderedListOutlined, UserOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Excel } from "antd-table-saveas-excel";
import userAccessService from "../../services/userAccess";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { useMediaQuery } from "react-responsive";
import TableActionBtnsComponent from "../../components/tableActionsBtns";

const { confirm } = Modal;

const formatDate = (dateString) => {
	if (!dateString) return "N/A";

	try {
		// If it's already in DD-MM-YYYY format, return as is
		if (
			typeof dateString === "string" &&
			dateString.match(/^\d{2}-\d{2}-\d{4}$/)
		) {
			return dateString;
		}

		// If it's in YYYY-MM-DD format, convert to DD-MM-YYYY
		if (
			typeof dateString === "string" &&
			dateString.match(/^\d{4}-\d{2}-\d{2}$/)
		) {
			const [year, month, day] = dateString.split("-");
			return `${day}-${month}-${year}`;
		}

		// If it's a Date object or ISO string
		const date = new Date(dateString);
		if (!isNaN(date.getTime())) {
			const day = String(date.getDate()).padStart(2, "0");
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const year = date.getFullYear();
			return `${day}-${month}-${year}`;
		}

		return dateString; // Return original if no pattern matches
	} catch (e) {
		console.error("Date formatting error:", e, "for date:", dateString);
		return dateString; // Return original if parsing fails
	}
};

const UserAccess = () => {
	const [searchForm] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [userAccessLoading, setUserAccessLoading] = useState(false);
	const [data, setData] = useState([]);
	const [visible, setVisible] = useState(false);
	const [formVisible, setFormVisible] = useState(false);
	const { currentUser } = useSelector((state) => state.user);
	const [formId, setFormId] = useState(9);
	const [searchValues, setSearchValues] = useState({});

	const allowedPermissions = currentUser?.["roles.permission"]?.map(
		(permission) => permission.name
	);

	const hasPermission = (permission) =>
		allowedPermissions?.includes(permission);

	const toggleFormVisibility = () => {
		setFormVisible(!formVisible);
	};


	// For Save State
	const uaLocalKey = currentUser?.user_id
	? `uaLocal_${currentUser.user_id}`
	: null;
	const UA_TTL_MS = 24 * 60 * 60 * 1000;

	const saveTableState = (state) => {
		if (!uaLocalKey) return;
		localStorage.setItem(uaLocalKey, JSON.stringify({ ...state, savedAt: Date.now() }));
	};

	const onSearch = async (values) => {
	setSearchValues(values);

	saveTableState({
		searchValues: values,
		page: 1,
		perPage: itemsPerPage,
		sortBy: "",
		sortOrder: "",
	});

	setUserAccessLoading(true);

	try {

		const response = await userAccessService.searchUserAccess(values);

		const apiData = response.data || response;

		setData(apiData.data || apiData);
		setCurrentPage(apiData.meta?.current_page || 1);
		setTotalDataCount(apiData.meta?.total || apiData.total);
		setItemsPerPage(apiData.meta?.per_page || apiData.per_page);
	} catch (error) {
		console.error(error);
		notification.error({
			message: "Search Error",
			description: "Failed to search records",
		});
	} finally {
		setUserAccessLoading(false);
	}
};

useEffect(() => {
	if (!uaLocalKey) return;

	const saved = localStorage.getItem(uaLocalKey);

	if (saved) {
		const parsed = JSON.parse(saved);
		if (parsed.savedAt && Date.now() - parsed.savedAt > UA_TTL_MS) {
			localStorage.removeItem(uaLocalKey);
			getUserAccess();
			return;
		}

		setSearchValues(parsed.searchValues || {});
		setCurrentPage(parsed.page || 1);
		setItemsPerPage(parsed.perPage || 15);

		// set form values
		searchForm.setFieldsValue(parsed.searchValues || {});

		// call API with saved state
		getUserAccess(
			parsed.page || 1,
			parsed.perPage || 15,
			parsed.sortBy || "",
			parsed.sortOrder || "",
			parsed.searchValues || {}
		);
	} else {
		getUserAccess();
	}
}, [uaLocalKey]);

	const columns = [
		{
			title: "Sequence No",
			dataIndex: "sequence_no",
			key: "sequence_no",
			width: 150,
			className: "resizable-column",
			sorter: true,
			fixed: useMediaQuery({ minWidth: 768 }) ? "left" : null,
			render: (text) => text || "N/A",
		},
		{
			title: "Request Title",
			dataIndex: "request_title",
			key: "request_title",
			width: 200,
			className: "resizable-column",
			sorter: true,
			render: (text) => text || "N/A",
		},
		// {
		// 	title: "Department",
		// 	dataIndex: "department",
		// 	key: "department",
		// 	width: 150,
		// 	sorter: true,
		// 	render: (department) => department?.name || "N/A",
		// },
		// {
		// 	title: "Location",
		// 	dataIndex: "location",
		// 	key: "location",
		// 	width: 150,
		// 	sorter: true,
		// 	render: (location) => location?.name || "N/A",
		// },
		{
			title: "Access Type",
			dataIndex: "access_type",
			key: "access_type",
			width: 120,
			sorter: true,
			render: (text) => {
				if (!text) return "N/A";
				// Convert to proper case
				return text.charAt(0).toUpperCase() + text.slice(1);
			},
		},
		{
			title: "Duration",
			dataIndex: "duration",
			key: "duration",
			width: 200,
			sorter: true,
			render: (text, record) => {
				if (record.start_date && record.end_date) {
					return `${formatDate(record.start_date)} to ${formatDate(
						record.end_date
					)}`;
				}
				return "N/A";
			},
		},
		{
			title: "Created By",
			dataIndex: "created_by",
			key: "created_by",
			width: 150,
			sorter: true,
			render: (created_by) => created_by?.name || "N/A",
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			width: 150,
			sorter: true,
			render: (text) => text || "N/A",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			sorter: true,
			width: 100,
			render: (status) => (
				<Tag
					color={
						(status === "Pending" && "orange") ||
						(status === "Approved" && "green") ||
						(status === "Return" && "red") ||
						(status === "Disapproved" && "red") ||
						(status === "Processing" && "blue") ||
						"default"
					}
				>
					{status || "N/A"}
				</Tag>
			),
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			fixed: useMediaQuery({ minWidth: 768 }) ? "right" : null,
			render: (text, record) =>
				record && (
					<TableActionBtnsComponent
						record={record}
						handleDelete={handleDelete}
						currentUser={currentUser}
						formId={formId}
					/>
				),
		},
	];

	const getUserAccess = async (
		page = 1,
		itemsPerPage = 15,
		sortBy = "",
		sortOrder = "",
		searchParams = null
	) => {
		setUserAccessLoading(true);
		try {
			let response;

			// Search or normal request
			if (searchParams && Object.keys(searchParams).length > 0) {
				response = await userAccessService.searchUserAccess({
					...searchParams,
					page,
					itemsPerPage,
					sortBy,
					sortOrder,
				});
			} else {
				response = await userAccessService.getUserAccess(
					page,
					itemsPerPage,
					sortBy,
					sortOrder
				);
			}

			const apiData = response.data || [];
			const meta = response.meta || {};

			// response.data is already the final array of items
			const tableData = apiData.map((item) => ({
				...item,
				key: item.id, // AntD requirement
			}));

			setData(tableData);
			setCurrentPage(meta.current_page || 1);
			setTotalDataCount(meta.total || tableData.length);
			setItemsPerPage(meta.per_page || itemsPerPage);
		} catch (error) {
			console.error("Failed to fetch data:", error);
			notification.error({
				message: "Error",
				description: "Failed to fetch user access records",
			});
		} finally {
			setUserAccessLoading(false);
		}
	};

	// const handleClick = () => {
	// 	const excelColumns = columns
	// 		.filter((column) => column.key !== "actions")
	// 		.map((column) => {
	// 			const { render, ...columnWithoutRender } = column;
	// 			return columnWithoutRender;
	// 		});

	// 	const excel = new Excel();
	// 	excel
	// 		.addSheet("User Access Data")
	// 		.addColumns(excelColumns)
	// 		.addDataSource(data, {
	// 			str2Percent: true,
	// 		})
	// 		.saveAs("UserAccess_Excel.xlsx");
	// };

	const handleExport = async () => {
		try {
			// Fetch all records (up to 1000 per page)
			const response = await userAccessService.getUserAccess(1, 1000); // page=1, itemsPerPage=1000
			const allData = response.data.map((item) => ({ ...item, key: item.id }));

			// Prepare columns excluding Actions
			const excelColumns = columns
			.filter((column) => column.key !== "actions")
			.map(({ render, ...colWithoutRender }) => colWithoutRender);

			// Export to Excel
			const excel = new Excel();
			excel
			.addSheet("User Access Data")
			.addColumns(excelColumns)
			.addDataSource(allData, { str2Percent: true })
			.saveAs("UserAccess_Excel.xlsx");
		} catch (error) {
			console.error("Error exporting all data:", error);
			notification.error({
			message: "Export Error",
			description: "Failed to export all user access records",
			});
		}
	};


	const [visibleColumns, setVisibleColumns] = useState(() => {
		try {
			const savedVisibleColumns = localStorage.getItem(
				"userAccessVisibleColumns"
			);
			if (savedVisibleColumns) {
				return JSON.parse(savedVisibleColumns);
			}
		} catch (error) {
			console.error("Error loading visibleColumns from local storage:", error);
		}
		return columns.map((column) => column.key);
	});

	const handleColumnToggle = (key) => {
		const updatedVisibleColumns = visibleColumns.includes(key)
			? visibleColumns.filter((colKey) => colKey !== key)
			: [...visibleColumns, key];

		setVisibleColumns(updatedVisibleColumns);

		try {
			localStorage.setItem(
				"userAccessVisibleColumns",
				JSON.stringify(updatedVisibleColumns)
			);
		} catch (error) {
			console.error("Error saving visibleColumns to local storage:", error);
		}
	};

	const handleDelete = (record) => {
		confirm({
			title: "Are you sure you want to delete this record?",
			content: "This action cannot be undone.",
			okText: "Yes",
			okType: "danger",
			cancelText: "No",
			onOk: async () => {
				try {
					const response = await userAccessService.deleteUserAccess(record.id);
					if (response.success) {
						notification.success({
							message: "Deleted Successfully",
							description: response.message,
						});
						// Refetch data for current page after deletion
						await getUserAccess(
							currentPage,
							itemsPerPage,
							null,
							null,
							searchValues
						);
					}
				} catch (error) {
					notification.error({
						message: "Error",
						description:
							error.response?.data?.message || "Failed to delete record.",
					});
				}
			},
			onCancel() {
				console.log("Delete action cancelled");
			},
		});
	};

	// useEffect(() => {
	// 	getUserAccess();
	// }, []);

	const menuItems = columns.map((column) => ({
		key: column.key,
		label: (
			<Checkbox
				className='mr-5'
				style={{ fontSize: "12px" }}
				onChange={() => handleColumnToggle(column.key)}
				checked={visibleColumns.includes(column.key)}
			>
				{column.title}
			</Checkbox>
		),
	}));

	const filteredColumns = columns.filter((column) =>
		visibleColumns.includes(column.key)
	);

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header
						icon={<UserOutlined />}
						title='User Access Requests'
						right={
							<>
							{/* 🔹 My Workflows Button */}
														 {hasPermission("OwnWorkflow-view") && (
																		  <Link to={`/own-workflow?form_id=9`}>
																		  <Button className='mr-10'>
																			My Workflows
																		  </Button>
																		  </Link>
																		)}
								<Button onClick={handleExport} className='mr-10'>
									Export
								</Button>

								<Link to={`/user-access-form/add/`}>
									<Button className='btn-blue' type='primary'>
										Add User Access Request
									</Button>
								</Link>
							</>
						}
					/>
				</Col>
			</Row>

			<Card className='mt-16'>
				<Button onClick={toggleFormVisibility}>
					{formVisible ? "Hide Search Form" : "Show Search Form"}
				</Button>

				{formVisible && (
					<Form
						form={searchForm}
						onFinish={onSearch}
						layout='vertical'
						className='mt-16'
					>
						<Card className='pt-6' style={{ paddingBottom: "0 !important" }}>
							<Row gutter={[12, 12]}>
								{[
									{ key: "sequence_no", placeholder: "Sequence No" },
									{ key: "request_title", placeholder: "Request Title" },
									{ key: "access_type", placeholder: "Access Type" },
									{ key: "usage_remarks", placeholder: "Usage Remarks" },
									{ key: "status", placeholder: "Status" },
								].map(({ key, placeholder }) => (
									<Col key={key} lg={6} md={12} sm={24} xs={24}>
										<Form.Item name={key}>
											<Input
												size='large'
												allowClear
												placeholder={`${placeholder}`}
											/>
										</Form.Item>
									</Col>
								))}
							</Row>
							<Row gutter={[12, 12]}>
								<Col lg={12} md={12} sm={12} xs={12}>
									<Button
										block
										className='btn-blue'
										htmlType='submit'
										type='primary'
									>
										Search
									</Button>
								</Col>
								<Col lg={12} md={12} sm={12} xs={12}>
									<Button
										block
										className='btn-blue'
										htmlType='button'
										type='primary'
										onClick={() => {
											const emptyVals = {
												sequence_no: undefined,
												request_title: undefined,
												access_type: undefined,
												usage_remarks: undefined,
												status: undefined,
											};
											searchForm.setFieldsValue(emptyVals);
											searchForm.resetFields();
											setSearchValues({});

											if (uaLocalKey) {
												localStorage.removeItem(uaLocalKey);
											}

											onSearch({});
										}}
									>
										Clear
									</Button>
								</Col>
							</Row>
						</Card>
					</Form>
				)}
			</Card>

			<Row className='mt-16'>
				<Col span={24}>
					<Card>
						<Dropdown
							menu={{ items: menuItems }}
							className='mr-10'
							open={visible}
							onOpenChange={(isVisible) => setVisible(isVisible)}
							trigger={["click"]}
						>
							<button
								style={{
									color: "gray",
									fontSize: "11px",
									background: "transparent",
									border: "none",
									padding: 0,
									cursor: "pointer",
								}}
								onClick={(e) => e.preventDefault()}
							>
								<UnorderedListOutlined /> Columns
							</button>
						</Dropdown>

						<Table
							size='small'
							columns={filteredColumns}
							style={{ minHeight: "100vh" }}
							dataSource={data.map((item) => ({ ...item, key: item.id }))}
							pagination={{
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (total, range) =>
									`${range[0]}-${range[1]} of ${total} items`,
								current: currentPage,
								total: totalDataCount,
								pageSize: itemsPerPage,
								pageSizeOptions: ["10", "15", "20", "50", "100"],
							}}
							loading={userAccessLoading}
							scroll={{
								x: 1400,
								y: "calc(100vh - 300px)",
							}}
							sticky={true}
							onChange={(pagination, filters, sorter) => {
								const { order, field } = sorter;
								const sortOrder =
									order === "ascend"
										? "asc"
										: order === "descend"
										? "desc"
										: "";

								saveTableState({
									searchValues,
									page: pagination.current || currentPage,
									perPage: pagination.pageSize || itemsPerPage,
									sortBy: field,
									sortOrder,
								});

								getUserAccess(
									pagination.current || currentPage,
									pagination.pageSize || itemsPerPage,
									field,
									sortOrder,
									searchValues
								);
							}}
						/>
					</Card>
				</Col>
			</Row>
		</DefaultLayout>
	);
};

export default UserAccess;
